import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, extractErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BrandLogo } from "@/components/BrandLogo";
import { toast } from "sonner";
import { Building2 } from "lucide-react";

export default function AgencyActivate() {
  const { token } = useParams();
  const nav = useNavigate();
  const { applyAuthenticatedUser } = useAuth();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [confirmName, setConfirmName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/agency/invite/${token}`)
      .then(({ data }) => { setInfo(data); setConfirmName(data.agency_name || ""); })
      .catch((e) => setErr(extractErrorMessage(e, "This invitation link is invalid or has expired.")));
  }, [token]);

  const activate = async () => {
    if (pw.length < 12) return toast.error("Password must be at least 12 characters.");
    if (pw !== pw2) return toast.error("Passwords do not match.");
    setBusy(true);
    try {
      const { data } = await api.post(`/agency/invite/${token}/activate`, { password: pw, confirm_agency_name: confirmName });
      applyAuthenticatedUser(data);
      toast.success("Your ARPS agency account is active.");
      nav("/agency");
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not activate your account."));
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-5 py-12">
      <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="agency-activate-card">
        <div className="mb-6 flex items-center gap-2.5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900 p-1"><BrandLogo className="h-full w-auto" /></div>
          <p className="font-display text-sm font-extrabold text-slate-900">Welcome to ARPS</p>
        </div>
        {err && <p className="text-sm text-red-600" data-testid="agency-activate-error">{err}</p>}
        {!err && !info && <p className="text-sm text-slate-500">Loading…</p>}
        {info && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-slate-700"><Building2 className="h-4 w-4" /><span className="text-sm font-semibold">{info.agency_name}</span></div>
            <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">Activate your account</h1>
            <p className="text-sm text-slate-600">Your agency has completed its onboarding and payment. Create your administrator password to enter ARPS.</p>
            <div>
              <Label htmlFor="cn">Agency name</Label>
              <Input id="cn" data-testid="agency-activate-name" value={confirmName} onChange={(e) => setConfirmName(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="em">Administrator email</Label>
              <Input id="em" value={info.email} disabled />
            </div>
            <div>
              <Label htmlFor="pw">Create password</Label>
              <Input id="pw" type="password" data-testid="agency-activate-password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 12 characters" />
            </div>
            <div>
              <Label htmlFor="pw2">Confirm password</Label>
              <Input id="pw2" type="password" data-testid="agency-activate-password-confirm" value={pw2} onChange={(e) => setPw2(e.target.value)} />
            </div>
            <Button data-testid="agency-activate-submit" onClick={activate} disabled={busy} className="w-full bg-sky-600 text-white hover:bg-sky-700">
              {busy ? "Activating…" : "Activate & Enter ARPS"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
